import React, { useState, useEffect } from "react";
import api from "../services/api";
import { format } from "date-fns";
import { FiFileText, FiDownload, FiExternalLink } from "react-icons/fi";

function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInvoices();
  }, [pagination.page]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/invoices?page=${pagination.page}`);
      setInvoices(response.data.data || []);
      setPagination((prev) => ({ ...prev, ...response.data.pagination }));
    } catch (err) {
      console.error("Failed to load invoices", err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = "usd") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const getStatusBadge = (status) => {
    const badges = {
      paid: "badge-success",
      open: "badge-warning",
      draft: "badge-info",
      uncollectible: "badge-error",
      void: "badge-error",
    };
    return badges[status] || "badge-info";
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Invoices</h1>
        <p className="page-subtitle">View and download your invoices</p>
      </div>

      <div className="card">
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : invoices.length > 0 ? (
          <>
            <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <FiFileText />#{invoice.id.slice(0, 8)}
                      </div>
                    </td>
                    <td>
                      {format(new Date(invoice.created_at), "MMM d, yyyy")}
                    </td>
                    <td style={{ fontWeight: 600 }}>
                      {formatCurrency(invoice.amount_due, invoice.currency)}
                    </td>
                    <td>
                      <span
                        className={`badge ${getStatusBadge(invoice.status)}`}
                      >
                        {invoice.status}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: "8px" }}>
                        {invoice.invoice_url && (
                          <a
                            href={invoice.invoice_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-outline btn-sm"
                          >
                            <FiExternalLink /> View
                          </a>
                        )}
                        {invoice.pdf_url && (
                          <a
                            href={invoice.pdf_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-outline btn-sm"
                          >
                            <FiDownload /> PDF
                          </a>
                        )}
                        {invoice.status === "open" && invoice.invoice_url && (
                          <a
                            href={invoice.invoice_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-primary btn-sm"
                          >
                            Pay Now
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>

            {/* Pagination */}
            <div className="flex-between mt-4" style={{ marginTop: "16px" }}>
              <span className="text-muted">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <div
                className="flex gap-2"
                style={{ display: "flex", gap: "8px" }}
              >
                <button
                  className="btn btn-outline"
                  disabled={pagination.page <= 1}
                  onClick={() =>
                    setPagination((prev) => ({ ...prev, page: prev.page - 1 }))
                  }
                >
                  Previous
                </button>
                <button
                  className="btn btn-outline"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() =>
                    setPagination((prev) => ({ ...prev, page: prev.page + 1 }))
                  }
                >
                  Next
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="empty-state">
            <FiFileText size={48} className="empty-state-icon" />
            <h3 className="empty-state-title">No Invoices</h3>
            <p className="empty-state-text">You don't have any invoices yet.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Invoices;
